
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Flame, TrendingUp } from "lucide-react";
import { Lead } from "@/types";

interface AnalyticsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  trend?: "up" | "down" | "neutral";
  className?: string;
}

const AnalyticsCard = ({ title, value, icon, description, trend, className }: AnalyticsCardProps) => {
  return (
    <Card className={`${className} shadow-md`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-5 w-5 text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
      {trend && (
        <CardFooter className="p-2 pt-0">
          <div className={`flex items-center text-xs ${
            trend === "up" ? "text-green-500" : 
            trend === "down" ? "text-red-500" : "text-gray-500"
          }`}>
            {trend === "up" && <TrendingUp className="mr-1 h-3 w-3" />}
            {trend === "down" && <TrendingUp className="mr-1 h-3 w-3 transform rotate-180" />}
            {trend === "neutral" ? "→ No change" : `${trend === "up" ? "↑" : "↓"} 20% from last period`}
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

interface DashboardAnalyticsProps {
  leads: Lead[];
}

const DashboardAnalytics = ({ leads }: DashboardAnalyticsProps) => {
  // Calculate analytics metrics
  const totalLeads = leads.length;
  
  const contactedLeads = leads.filter(lead => 
    lead.status === "completed" || 
    lead.status === "reactivated" || 
    lead.status === "not-interested"
  ).length;
  
  const reheatedLeads = leads.filter(lead => 
    lead.status === "reactivated"
  ).length;
  
  // Calculate reheat rate (reheated leads / total contacted)
  const reheatRate = contactedLeads > 0 
    ? Math.round((reheatedLeads / contactedLeads) * 100) 
    : 0;

  return (
    <div className="grid gap-6 md:grid-cols-3">
      <AnalyticsCard
        title="Total Leads"
        value={totalLeads}
        icon={<Upload className="h-5 w-5" />}
        description="All uploaded leads"
        className="p-2"
      />
      <AnalyticsCard
        title="Leads Reheated"
        value={reheatedLeads}
        icon={<Flame className="h-5 w-5" />}
        description="Successfully reactivated leads"
        className="p-2"
      />
      <AnalyticsCard
        title="Reheat Rate"
        value={`${reheatRate}%`}
        icon={<TrendingUp className="h-5 w-5" />}
        description={`${reheatedLeads} of ${contactedLeads} leads contacted`}
        trend={reheatRate > 50 ? "up" : reheatRate < 30 ? "down" : "neutral"}
        className="p-2"
      />
    </div>
  );
};

export default DashboardAnalytics;
